import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Grid, Card, CardContent,
  CircularProgress, Alert, Chip
} from '@mui/material';
import { AttachMoney, TrendingUp, People, ShoppingCart } from '@mui/icons-material';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';

interface RevenueImpactAnalysisProps {
  startDate: string;
  endDate: string;
}

const RevenueImpactAnalysis: React.FC<RevenueImpactAnalysisProps> = ({ startDate, endDate }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    // Mock data
    setTimeout(() => {
      setData({
        total_revenue_influenced: 125000,
        conversations_with_revenue: 45,
        avg_revenue_per_conversation: 2778,
        revenue_by_channel: [
          { channel: 'Email', revenue: 45000, conversations: 18 },
          { channel: 'Chat', revenue: 52000, conversations: 20 },
          { channel: 'Phone', revenue: 28000, conversations: 7 }
        ],
        revenue_trend: Array.from({ length: 30 }, (_, i) => ({
          date: new Date(Date.now() - (29 - i) * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          revenue: 3000 + Math.random() * 2000
        }))
      });
      setLoading(false);
    }, 500);
  }, [startDate, endDate]);

  if (loading) return <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>;
  if (!data) return <Alert severity="info">No revenue data available</Alert>;

  const revenueChartOptions: ApexOptions = {
    chart: { type: 'area', toolbar: { show: false } },
    dataLabels: { enabled: false },
    stroke: { curve: 'smooth', width: 3 },
    fill: { type: 'gradient', gradient: { shadeIntensity: 1, opacityFrom: 0.7, opacityTo: 0.3 } },
    xaxis: { categories: data.revenue_trend.map((d: any) => d.date), labels: { rotate: -45 } },
    yaxis: { labels: { formatter: (val: number) => `$${val.toFixed(0)}` } },
    colors: ['#4caf50'],
    tooltip: { y: { formatter: (val: number) => `$${val.toLocaleString()}` } }
  };

  return (
    <Box>
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>Total Revenue Influenced</Typography>
              <Typography variant="h4" color="primary">${data.total_revenue_influenced.toLocaleString()}</Typography>
              <Typography variant="caption" color="textSecondary">Through support conversations</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>Conversations with Revenue</Typography>
              <Typography variant="h4" color="primary">{data.conversations_with_revenue}</Typography>
              <Chip label="+12%" size="small" color="success" icon={<TrendingUp />} sx={{ mt: 1 }} />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" variant="body2" gutterBottom>Avg Revenue/Conversation</Typography>
              <Typography variant="h4" color="primary">${data.avg_revenue_per_conversation.toLocaleString()}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ bgcolor: '#e8f5e9' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AttachMoney color="success" />
                <Box>
                  <Typography color="textSecondary" variant="body2">Revenue Trend</Typography>
                  <Typography variant="h4" color="success.main">↑ 15%</Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Revenue Trend (30 Days)</Typography>
        <Chart
          options={revenueChartOptions}
          series={[{ name: 'Revenue', data: data.revenue_trend.map((d: any) => d.revenue) }]}
          type="area"
          height={300}
        />
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>Revenue by Channel</Typography>
        <Grid container spacing={2}>
          {data.revenue_by_channel.map((channel: any) => (
            <Grid item xs={12} md={4} key={channel.channel}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1" fontWeight="bold">{channel.channel}</Typography>
                  <Typography variant="h5" color="primary">${channel.revenue.toLocaleString()}</Typography>
                  <Typography variant="caption">{channel.conversations} conversations</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="subtitle2">💡 Revenue Attribution</Typography>
        <Typography variant="caption">Revenue is attributed when conversations lead to purchases within 30 days. Data tracked via conversation tags and CRM integration.</Typography>
      </Alert>
    </Box>
  );
};

export default RevenueImpactAnalysis;
